import * as React from "react";
import { cn } from "../utils";
import { GridOverlay } from "./grid-overlay";

/**
 * The 12-column page field (docs/design/prototypes/redesign/SYSTEM.md §2 — the spine).
 *
 * A page's content area IS a Band: sections are its DIRECT children, defaulting
 * to the full field (`grid-column: 1 / -1` via the zero-specificity `.rg-band`
 * rule in the app globals) and placing on shared column lines with `<Col>` or
 * col-span utilities. Splits land on shared lines (6+6, 8+4, 7+5); two stacked
 * sections may never invent different widths or gutters. Grid line 1 is the
 * content edge — every eyebrow, ledger first column, and metric cell sits on it
 * (bordered surfaces get there by hanging: `hangClass` / `.rg-hang`).
 *
 * The Band carries the `?grid=1` / `g`-key verification overlay so alignment is
 * checkable on every page that uses it (`overlay={false}` for a nested Band).
 */
export function Band({
  className,
  children,
  id,
  overlay = true,
}: {
  className?: string;
  children: React.ReactNode;
  id?: string;
  /** Mount the ?grid=1 / g-key verification overlay (default true). */
  overlay?: boolean;
}) {
  return (
    <div id={id} className={cn("grid-field rg-band relative gap-y-10", className)}>
      {children}
      {overlay ? <GridOverlay /> : null}
    </div>
  );
}

/** Col — places a Band child by column LINE: start+end (1-based, end exclusive —
 *  13 = the last line) or a span. */
export function Col({
  start,
  end,
  span,
  className,
  children,
}: {
  /** 1-based column line the block starts on. */
  start?: number;
  /** 1-based column line the block ends on (exclusive edge, e.g. 13 = last). */
  end?: number;
  /** Number of columns to span (ignored when start+end given). */
  span?: number;
  className?: string;
  children: React.ReactNode;
}) {
  const gridColumn =
    start != null && end != null
      ? `${start} / ${end}`
      : span != null
        ? `span ${span} / span ${span}`
        : undefined;
  return (
    <div className={className} style={gridColumn ? { gridColumn } : undefined}>
      {children}
    </div>
  );
}

/**
 * The class that hangs a bordered surface into the gutter by its own first-cell
 * inset (1px border + 1rem cell padding = 17px) so its CONTENT lands on the
 * grid line while its chrome bleeds (SYSTEM.md §2.3). `"both"` for a full-width
 * surface; `"left"`/`"right"` for a surface in a split row (hang the OUTER edge
 * only — two full hangs collide in a shared gutter); `false` opts out.
 */
export function hangClass(
  hang: "both" | "left" | "right" | false = "both",
): string | undefined {
  return hang === "both"
    ? "rg-hang"
    : hang === "left"
      ? "rg-hang-l"
      : hang === "right"
        ? "rg-hang-r"
        : undefined;
}
